import React, { useState, useEffect, useContext } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { RadioGroup } from '@atlaskit/radio';
import Button from '@atlaskit/button/new';
import Link from '@atlaskit/link';
import Form, { Field, FormFooter } from '@atlaskit/form';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner';
import { FlagsProvider } from '@atlaskit/flag';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { FrictionModal } from './dialogs/FrictionModal.tsx';
import {
	useConfluenceSiteTeamConfigurations,
	ConfluenceTeamAdminControlsMode,
} from './useConfluenceSiteTeamConfigurations.tsx';

const i18n = defineMessages({
	title: {
		id: 'team-admin-controls.admin-controls.page.title',
		defaultMessage: 'Teams',
		description: 'Page title for the admin controls page',
	},
	manageTeamsButton: {
		id: 'team-admin-controls.admin-controls.page.manage-teams-button',
		defaultMessage: 'Manage teams',
		description: 'Button to manage teams',
	},
	subtextPrefix: {
		id: 'team-admin-controls.admin-controls.page.subtext-prefix',
		defaultMessage:
			'By default, users on this site can use teams to manage access to spaces and content. You can choose which team types can be assigned access.',
		description: 'Subtext for the admin controls page',
	},
	learnMoreLink: {
		id: 'team-admin-controls.admin-controls.page.learn-more-link',
		defaultMessage: 'Learn more',
		description: 'Learn more link',
	},
	radioGroupLabel: {
		id: 'team-admin-controls.admin-controls.page.radio-group-label',
		defaultMessage: 'Which teams can be assigned access?',
		description: 'Label for the radio group',
	},
	radioGroupOption1: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-1',
		defaultMessage: 'Any team type',
		description: 'Option 1 for the radio group',
	},
	radioGroupOption2: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-2',
		defaultMessage: 'Only verified teams',
		description: 'Option 2 for the radio group',
	},
	radioGroupOption3: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-3',
		defaultMessage: 'No teams (disable teams in permissions)',
		description: 'Option 3 for the radio group',
	},
	membershipControlRadioGroupLabel: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-label',
		defaultMessage: 'Membership control',
		description: 'Label for the membership control radio group',
	},
	membershipControlRadioGroupOption1: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-option-1',
		defaultMessage: 'Allow teams that anyone can join',
		description: 'Option 1 for the membership control radio group',
	},
	membershipControlRadioGroupOption2: {
		id: 'team-admin-controls.admin-controls.page.membership-control-radio-group-option-2',
		defaultMessage: 'Only allow teams that require approval to join',
		description: 'Option 2 for the membership control radio group',
	},
	saveButton: {
		id: 'team-admin-controls.admin-controls.page.save-button',
		defaultMessage: 'Save',
		description: 'Save button',
	},
	cancelButton: {
		id: 'team-admin-controls.admin-controls.page.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Cancel button',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.800'),
		paddingInline: token('space.800'),
		maxWidth: '1000px',
		marginInline: 'auto',
	},
});

const radioOptions = [
	{
		value: ConfluenceTeamAdminControlsMode.ALL_TEAMS,
		label: <FormattedMessage {...i18n.radioGroupOption1} />,
	},
	{
		value: ConfluenceTeamAdminControlsMode.VERIFIED_TEAMS,
		label: <FormattedMessage {...i18n.radioGroupOption2} />,
	},
	{
		value: ConfluenceTeamAdminControlsMode.NO_TEAMS,
		label: <FormattedMessage {...i18n.radioGroupOption3} />,
	},
];

const membershipControlRadioOptions = [
	{
		value: 'open-membership',
		label: <FormattedMessage {...i18n.membershipControlRadioGroupOption1} />,
	},
	{
		value: ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS,
		label: <FormattedMessage {...i18n.membershipControlRadioGroupOption2} />,
	},
];

const AdminControlsContent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, orgId } = useSessionData();
	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	const onClickManageTeams = () => {
		window.open(href, '_blank');
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'clicked manage teams button',
			},
		}).fire();
	};

	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		siteConfigurationLoading,
		currentTeamAdminControlsMode,
		updateTeamAdminControlsSettings,
	} = useConfluenceSiteTeamConfigurations();
	const [hasChanged, setHasChanged] = useState(false);
	const [membershipControlType, setMembershipControlType] = useState('open-membership');
	const [showFrictionModal, setShowFrictionModal] = useState(false);
	const [selectedTeamAccessType, setSelectedTeamAccessType] = useState<
		ConfluenceTeamAdminControlsMode | undefined
	>(undefined);

	useEffect(() => {
		if (!siteConfigurationLoading && currentTeamAdminControlsMode) {
			if (currentTeamAdminControlsMode === ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS) {
				setSelectedTeamAccessType(ConfluenceTeamAdminControlsMode.ALL_TEAMS);
				setMembershipControlType(ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS);
			} else {
				setSelectedTeamAccessType(currentTeamAdminControlsMode as ConfluenceTeamAdminControlsMode);
			}
		}
	}, [siteConfigurationLoading, currentTeamAdminControlsMode]);

	const onTeamAccessTypeChange = (value: string) => {
		setHasChanged(true);
		setSelectedTeamAccessType(value as ConfluenceTeamAdminControlsMode);
	};

	const onMembershipControlTypeChange = (value: string) => {
		setHasChanged(true);
		setMembershipControlType(value);
	};

	const onSaveClick = () => {
		if (
			selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS &&
			membershipControlType === 'open-membership'
		) {
			void onConfirmFrictionModal();
		} else {
			setShowFrictionModal(true);
		}
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'saved team access type',
				attributes: {
					teamAccessType: selectedTeamAccessType,
					membershipControlType,
				},
			},
		}).fire();
	};

	const onConfirmFrictionModal = async () => {
		setShowFrictionModal(false);
		experienceTracker.start({
			name: 'updateTeamAdminControlsSettings',
		});
		if (
			selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS &&
			membershipControlType === ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS
		) {
			await updateTeamAdminControlsSettings(ConfluenceTeamAdminControlsMode.MEMBER_MANAGED_TEAMS);
		} else {
			await updateTeamAdminControlsSettings(
				selectedTeamAccessType as ConfluenceTeamAdminControlsMode,
			);
		}
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'adminControls',
				actionSubject: 'adminControls',
				action: 'confirmed friction modal',
			},
		}).fire();
	};
	return siteConfigurationLoading ? (
		<Spinner />
	) : (
		<Box xcss={styles.container}>
			{showFrictionModal && selectedTeamAccessType && (
				<FrictionModal
					width="medium"
					teamAccessType={selectedTeamAccessType}
					onConfirm={() => onConfirmFrictionModal()}
					onClose={() => setShowFrictionModal(false)}
				/>
			)}
			<Stack space="space.300">
				{/* Header with title and Manage teams button */}
				<Flex justifyContent="space-between" alignItems="center">
					<Heading size="large">{formatMessage(i18n.title)}</Heading>
					<Button
						iconBefore={LinkExternalIcon}
						appearance="subtle"
						onClick={() => onClickManageTeams()}
					>
						{formatMessage(i18n.manageTeamsButton)}
					</Button>
				</Flex>

				{/* Subtext with Learn more link */}
				<Text>
					{formatMessage(i18n.subtextPrefix)}{' '}
					<Link href="https://confluence.atlassian.com/conf59/manage-teams-1024324379.html">
						{formatMessage(i18n.learnMoreLink)}
					</Link>
				</Text>

				{/* Form with radio group */}
				<Box paddingBlockStart="space.300">
					<Form
						onSubmit={() => {
							// TODO: Implement save functionality
							return Promise.resolve();
						}}
					>
						{({ formProps }) => (
							<form {...formProps}>
								<Stack space="space.200" spread="space-between">
									<Heading size="small">{formatMessage(i18n.radioGroupLabel)}</Heading>
									{siteConfigurationLoading ? (
										<Spinner />
									) : (
										<Field name="teamAccessType" defaultValue={currentTeamAdminControlsMode}>
											{({ fieldProps }) => (
												<Stack space="space.150" spread="space-between">
													<RadioGroup
														{...fieldProps}
														options={radioOptions}
														onChange={(e) =>
															onTeamAccessTypeChange(
																e.target.value as ConfluenceTeamAdminControlsMode,
															)
														}
														value={selectedTeamAccessType}
													/>
												</Stack>
											)}
										</Field>
									)}
									{selectedTeamAccessType === ConfluenceTeamAdminControlsMode.ALL_TEAMS && (
										<>
											<Heading size="small">
												{formatMessage(i18n.membershipControlRadioGroupLabel)}
											</Heading>
											<Field name="membershipControlType" defaultValue={membershipControlType}>
												{({ fieldProps: membershipFieldProps }) => (
													<Stack space="space.150" spread="space-between">
														<RadioGroup
															{...membershipFieldProps}
															options={membershipControlRadioOptions}
															onChange={(e) => onMembershipControlTypeChange(e.target.value)}
															value={membershipControlType}
														/>
													</Stack>
												)}
											</Field>
										</>
									)}
								</Stack>
								<FormFooter>
									<Button
										type="submit"
										isDisabled={!hasChanged}
										appearance="primary"
										onClick={() => onSaveClick()}
									>
										{formatMessage(i18n.saveButton)}
									</Button>
									<Button
										type="button"
										appearance="subtle"
										onClick={() => setShowFrictionModal(false)}
									>
										{formatMessage(i18n.cancelButton)}
									</Button>
								</FormFooter>
							</form>
						)}
					</Form>
				</Box>
			</Stack>
		</Box>
	);
};

export const AdminControls = () => (
	<FlagsProvider>
		<AdminControlsContent />
	</FlagsProvider>
);
